<template>
  <div class="dea-buttons-share dea-card-resource__share-dropdown dea-border-grey-medium-light">
    <slot name="content" />
    <ErrorMessage v-if="warningText">{{ warningText }}</ErrorMessage>
    <span class="dea-card-resource__share-dropdown-label">Condividi su:</span>
    <div class="dea-card-resource__buttons-list">
      <div v-if="config.enableClassroom" class="dea-card-resource__button-box">
        <Buttons class="dea-button--outline" type="button" @click.stop>
          <div :class="classRoomShareClass">&nbsp;</div>
        </Buttons>
        <div class="dea-card-resource__button-box-label dea-heading--regular__xm">Classroom</div>
      </div>
      <div v-if="config.enableWeSchool" class="dea-card-resource__button-box">
        <Buttons class="dea-button--outline" type="button" @click.stop="openLink(weSchoolShare)">
          <Logos :src="weSchoolLogo" alt="Logo weSchool" aria-label="Logo weSchool" />
        </Buttons>
        <div class="dea-card-resource__button-box-label dea-heading--regular__xm">WeSchool</div>
      </div>
      <div v-if="config.enableTeams" class="dea-card-resource__button-box">
        <Buttons class="dea-button--outline" type="button" @click.stop>
          <div class="teams-share-button" :data-href="encodeURI(sharedObject.share_url)" data-icon-px-size="100"></div>
        </Buttons>
        <div class="dea-card-resource__button-box-label dea-heading--regular__xm">Teams</div>
      </div>
      <div v-if="config.enableBsmart" class="dea-card-resource__button-box">
        <Buttons class="dea-button--outline" type="button" @click.stop="openLink(bSmartShareUrl)">
          <Logos :src="bSmartLogo" alt="Logo bSmart" aria-label="Logo bSmart" />
        </Buttons>
        <div class="dea-card-resource__button-box-label dea-heading--regular__xm">bSmart</div>
      </div>
    </div>

    <Buttons
      class="dea-button--outline dea-button--medium dea-button--full"
      :class="`js-button-copy-${objectId}`"
      type="button"
      icon="minisito-risorse-link"
      @click.prevent.stop="updateClipboard(sharedObject.share_url)"
    >
      Copia il link
    </Buttons>
    <Tooltip
      v-if="showTooltip"
      class="dea-card-resource__tooltip-share-link"
      :class="{
        'dea-card-resource__tooltip-share-link--visible': showTooltip,
        'dea-card-resource__tooltip-share-link--left': getTooltipSide() === 'left',
        'dea-card-resource__tooltip-share-link--right': getTooltipSide() === 'right',
      }"
      :side="getTooltipSide()"
      :position="getPosition()"
      :negative="false"
    >
      <p class="dea-font-white">Link copiato!</p>
    </Tooltip>
    <input v-if="sharedObject.share_url" :class="`clipboard-${objectId}`" name="clipboard" style="display: none" />
  </div>
</template>

<script>
import Buttons from "@/components/elements/Buttons";
import Tooltip from "@/components/elements/Tooltip";
import Logos from "@/components/elements/Logos";
import weSchoolLogo from "@/assets/logo/share/WeSchool.svg";
import bSmartLogo from "@/assets/logo/share/bSmart.svg";
import ErrorMessage from "@/components/elements/ErrorMessage";

const tooltipWidth = 160;

export default {
  name: "ShareButtons",
  components: { Buttons, ErrorMessage, Logos, Tooltip },
  props: {
    sharedObject: {
      type: Object,
      required: true,
    },
    config: {
      type: Object,
      required: true,
    },
    warningText: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      showTooltip: false,
      weSchoolLogo,
      bSmartLogo,
      weSchoolShare: `${this.config.weSchoolShareUrl}${encodeURI(this.sharedObject.share_url)}`,
      bSmartShareUrl: `${this.config.bSmartShareUrl}${encodeURI(this.sharedObject.share_url)}`,
    };
  },
  computed: {
    classRoomShareClass() {
      return `classroom-share-button-${this.objectId}`;
    },
    objectId() {
      try {
        return this.sharedObject.resource_uuid;
      } catch (error) {
        return null;
      }
    },
  },
  mounted() {
    try {
      // eslint-disable-next-line no-undef
      if (shareToMicrosoftTeams) shareToMicrosoftTeams.renderButtons();
    } catch {
      // eslint-disable-next-line no-unused-expressions
      null;
    }
    // eslint-disable-next-line no-underscore-dangle
    window.___gcfg = {
      parsetags: "explicit",
    };
    this.renderWidget();

    document.addEventListener("click", () => {
      // eslint-disable-next-line no-restricted-globals
      const targetEl = event.target;
      const currentEl = document.getElementById("dea-buttons-share");
      if (!targetEl.isEqualNode(currentEl)) this.$emit("close-dropdown", false);
    });
  },
  methods: {
    getTooltipSide() {
      const el = document.querySelector(`.js-button-copy-${this.objectId}`);
      if (el) {
        // space available between the right border of the button and the viewport right margin
        const spaceRight = window.innerWidth - el.getBoundingClientRect().right;
        // space available between the left border of the button and the viewport left margin
        const spaceLeft = el.getBoundingClientRect().left;

        if (spaceRight <= tooltipWidth && spaceLeft <= tooltipWidth) {
          return "bottom";
        }

        if (spaceRight >= spaceLeft) {
          return "left";
        }
        if (spaceLeft > spaceRight) {
          return "right";
        }
      }
      return "";
    },
    getPosition() {
      return "center";
    },
    updateClipboard(link) {
      const cb = document.querySelector(`.clipboard-${this.objectId}`);
      cb.value = link;
      cb.style.display = "block";
      cb.select();
      document.execCommand("copy");
      cb.style.display = "none";
      this.showTooltip = true;
      setTimeout(() => {
        this.showTooltip = false;
      }, 2000);
    },
    openLink(link) {
      this.$emit("close-dropdown", false);
      if (!link) {
        return;
      }
      window.open(link, "_blank");
    },
    renderWidget() {
      const classRoomElement = document.querySelector(`.classroom-share-button-${this.objectId}`);
      // eslint-disable-next-line no-undef
      gapi.sharetoclassroom.render(classRoomElement, {
        locale: "it-IT",
        size: 100,
        title: this.sharedObject.title,
        url: encodeURI(this.sharedObject.share_url),
      });
    },
  },
};
</script>

<style lang="scss">
@import "@/styles/system.utils.scss"; //sass-lint:disable-line clean-import-paths

$dropdown-share-width: 250px;
$dropdown-share-max-width: 325px;
$tooltip-width: 160px;
$tooltip-bottom: 65px;
$logo-size: 100px;

.dea-card-resource {
  &__share-dropdown {
    background: $dea-color-white;
    border: $border-light;
    border-radius: $border-radius-medium;
    display: flex;
    flex-direction: column;
    margin-top: $space-x-large;
    max-width: $dropdown-share-max-width;
    min-width: $dropdown-share-width;
    padding: $space-xm-small;
    position: absolute;
    z-index: 1;

    &-label {
      margin-bottom: $space-xx-small;
    }

    .dea-button {
      margin-bottom: $space-x-small;

      &:hover,
      &:focus {
        box-shadow: none;
      }

      &__label {
        align-items: center;
        display: flex;

        /* The following style is for the iframe included for google classroom share */
        div {
          margin-right: $space-xx-small !important; // sass-lint:disable-line no-important
        }

        .svg-logo {
          height: $logo-size;
        }
      }

      &__icon {
        margin-right: $space-xx-small;
      }
    }

    .dea-error {
      .dea-ico-error {
        font-size: inherit;
      }

      margin: 0;
      margin-bottom: $space-x-small;
    }
  }

  &__buttons-list {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: $space-base;
    margin-top: $space-small;
  }

  &__button-box {
    height: $logo-size;
    margin-bottom: $space-intermediate;
    width: 50%;

    .dea-button {
      border: 0;
      height: $logo-size;
      margin: 0 auto $space-x-small;
    }
  }

  &__button-box-label {
    padding-left: $space-x-small;
    text-align: center;
  }

  &__tooltip-share-link {
    @include media("<=xs") {
      bottom: $tooltip-bottom;
      left: auto;
    }
    bottom: 0;
    display: none;
    position: absolute;
    top: unset !important; // sass-lint:disable-line no-important

    &--visible {
      display: block;
    }

    &--right {
      left: -$space-xx-large;
    }

    &--left {
      left: unset;
      right: -$space-xx-large;
    }

    .dea-tooltip__inner {
      background: $dea-color-grey-dark !important; // sass-lint:disable-line no-important
    }
  }
}
</style>
